/**
 * Core API - Factory Structure
 * 
 * Database operations for factory areas, production lines, and stations.
 * These form the Unified Namespace (UNS) hierarchy.
 */

import { supabase } from '@/integrations/supabase/client';

// =============================================================================
// Types
// =============================================================================

export interface FactoryArea {
  id: string;
  tenant_id: string;
  factory_id: string;
  name: string;
  description: string | null;
  color: string;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ProductionLine {
  id: string;
  tenant_id: string;
  factory_id: string;
  area_id: string;
  name: string;
  description: string | null;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface Station {
  id: string;
  tenant_id: string;
  factory_id: string;
  line_id: string;
  name: string;
  description: string | null;
  station_type: string | null;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// =============================================================================
// Queries - Factory Areas
// =============================================================================

/**
 * Fetches all active areas for a factory.
 */
export async function fetchFactoryAreas(factoryId: string): Promise<FactoryArea[]> {
  const { data, error } = await supabase
    .schema('core')
    .from('factory_areas')
    .select('*')
    .eq('factory_id', factoryId)
    .eq('is_active', true)
    .order('sort_order');

  if (error) throw error;
  return data;
}

/**
 * Fetches all areas for a factory (including inactive).
 */
export async function fetchAllFactoryAreas(factoryId: string): Promise<FactoryArea[]> {
  const { data, error } = await supabase
    .schema('core')
    .from('factory_areas')
    .select('*')
    .eq('factory_id', factoryId)
    .order('sort_order');

  if (error) throw error;
  return data;
}

/**
 * Fetches area names for display (minimal data).
 */
export async function fetchAreaNames(factoryId: string): Promise<{ id: string; name: string }[]> {
  const { data, error } = await supabase
    .schema('core')
    .from('factory_areas')
    .select('id, name')
    .eq('factory_id', factoryId)
    .eq('is_active', true)
    .order('sort_order');

  if (error) throw error;
  return data;
}

// =============================================================================
// Queries - Production Lines
// =============================================================================

/**
 * Fetches all active production lines for a factory.
 */
export async function fetchProductionLines(factoryId: string): Promise<ProductionLine[]> {
  const { data, error } = await supabase
    .schema('core')
    .from('production_lines')
    .select('*')
    .eq('factory_id', factoryId)
    .eq('is_active', true)
    .order('sort_order');

  if (error) throw error;
  return data;
}

/**
 * Fetches all production lines for a factory (including inactive).
 */
export async function fetchAllProductionLines(factoryId: string): Promise<ProductionLine[]> {
  const { data, error } = await supabase
    .schema('core')
    .from('production_lines')
    .select('*')
    .eq('factory_id', factoryId)
    .order('sort_order');

  if (error) throw error;
  return data;
}

/**
 * Fetches production lines for a specific area.
 */
export async function fetchProductionLinesByArea(areaId: string): Promise<ProductionLine[]> {
  const { data, error } = await supabase
    .schema('core')
    .from('production_lines')
    .select('*')
    .eq('area_id', areaId)
    .eq('is_active', true)
    .order('sort_order');

  if (error) throw error;
  return data;
}

/**
 * Fetches line names with area reference (minimal data).
 */
export async function fetchLineNames(factoryId: string): Promise<{ id: string; name: string; area_id: string }[]> {
  const { data, error } = await supabase
    .schema('core')
    .from('production_lines')
    .select('id, name, area_id')
    .eq('factory_id', factoryId)
    .eq('is_active', true)
    .order('sort_order');

  if (error) throw error;
  return data;
}

// =============================================================================
// Queries - Stations
// =============================================================================

/**
 * Fetches all active stations for a factory.
 */
export async function fetchStations(factoryId: string): Promise<Station[]> {
  const { data, error } = await supabase
    .schema('core')
    .from('stations')
    .select('*')
    .eq('factory_id', factoryId)
    .eq('is_active', true)
    .order('sort_order');

  if (error) throw error;
  return data;
}

/**
 * Fetches all stations for a factory (including inactive).
 */
export async function fetchAllStations(factoryId: string): Promise<Station[]> {
  const { data, error } = await supabase
    .schema('core')
    .from('stations')
    .select('*')
    .eq('factory_id', factoryId)
    .order('sort_order');

  if (error) throw error;
  return data;
}

/**
 * Fetches stations for a specific production line.
 */
export async function fetchStationsByLine(lineId: string): Promise<Station[]> {
  const { data, error } = await supabase
    .schema('core')
    .from('stations')
    .select('*')
    .eq('line_id', lineId)
    .eq('is_active', true)
    .order('sort_order');

  if (error) throw error;
  return data;
}

/**
 * Fetches station names with line reference (minimal data).
 */
export async function fetchStationNames(factoryId: string): Promise<{ id: string; name: string; line_id: string }[]> {
  const { data, error } = await supabase
    .schema('core')
    .from('stations')
    .select('id, name, line_id')
    .eq('factory_id', factoryId)
    .eq('is_active', true)
    .order('sort_order');

  if (error) throw error;
  return data;
}

// =============================================================================
// Mutations - Factory Areas
// =============================================================================

/**
 * Creates a new factory area.
 */
export async function createFactoryArea(
  area: Omit<FactoryArea, 'id' | 'created_at' | 'updated_at'>
): Promise<FactoryArea> {
  const { data, error } = await supabase
    .schema('core')
    .from('factory_areas')
    .insert(area)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Updates a factory area.
 */
export async function updateFactoryArea(
  areaId: string,
  updates: Partial<Omit<FactoryArea, 'id' | 'tenant_id' | 'factory_id' | 'created_at' | 'updated_at'>>
): Promise<void> {
  const { error } = await supabase
    .schema('core')
    .from('factory_areas')
    .update(updates)
    .eq('id', areaId);

  if (error) throw error;
}

/**
 * Deletes a factory area (hard delete).
 */
export async function deleteFactoryArea(areaId: string): Promise<void> {
  const { error } = await supabase
    .schema('core')
    .from('factory_areas')
    .delete()
    .eq('id', areaId);

  if (error) throw error;
}

// =============================================================================
// Mutations - Production Lines
// =============================================================================

/**
 * Creates a new production line.
 */
export async function createProductionLine(
  line: Omit<ProductionLine, 'id' | 'created_at' | 'updated_at'>
): Promise<ProductionLine> {
  const { data, error } = await supabase
    .schema('core')
    .from('production_lines')
    .insert(line)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Updates a production line.
 */
export async function updateProductionLine(
  lineId: string,
  updates: Partial<Omit<ProductionLine, 'id' | 'tenant_id' | 'factory_id' | 'created_at' | 'updated_at'>>
): Promise<void> {
  const { error } = await supabase
    .schema('core')
    .from('production_lines')
    .update(updates)
    .eq('id', lineId);

  if (error) throw error;
}

/**
 * Deletes a production line (hard delete).
 */
export async function deleteProductionLine(lineId: string): Promise<void> {
  const { error } = await supabase
    .schema('core')
    .from('production_lines')
    .delete()
    .eq('id', lineId);

  if (error) throw error;
}

// =============================================================================
// Mutations - Stations
// =============================================================================

/**
 * Creates a new station.
 */
export async function createStation(
  station: Omit<Station, 'id' | 'created_at' | 'updated_at'>
): Promise<Station> {
  const { data, error } = await supabase
    .schema('core')
    .from('stations')
    .insert(station)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Updates a station.
 */
export async function updateStation(
  stationId: string,
  updates: Partial<Omit<Station, 'id' | 'tenant_id' | 'factory_id' | 'created_at' | 'updated_at'>>
): Promise<void> {
  const { error } = await supabase
    .schema('core')
    .from('stations')
    .update(updates)
    .eq('id', stationId);

  if (error) throw error;
}

/**
 * Deletes a station (hard delete).
 */
export async function deleteStation(stationId: string): Promise<void> {
  const { error } = await supabase
    .schema('core')
    .from('stations')
    .delete()
    .eq('id', stationId);

  if (error) throw error;
}

// =============================================================================
// Mutations - Reordering
// =============================================================================

/**
 * Reorders production lines by updating their sort_order.
 * @param orderedIds - Array of line IDs in the new order
 */
export async function reorderProductionLines(orderedIds: string[]): Promise<void> {
  // Update each line's sort_order based on its new position
  for (let i = 0; i < orderedIds.length; i++) {
    const { error } = await supabase
      .schema('core')
      .from('production_lines')
      .update({ sort_order: i })
      .eq('id', orderedIds[i]);

    if (error) throw error;
  }
}

/**
 * Reorders stations by updating their sort_order.
 * @param orderedIds - Array of station IDs in the new order
 */
export async function reorderStations(orderedIds: string[]): Promise<void> {
  // Update each station's sort_order based on its new position
  for (let i = 0; i < orderedIds.length; i++) {
    const { error } = await supabase
      .schema('core')
      .from('stations')
      .update({ sort_order: i })
      .eq('id', orderedIds[i]);

    if (error) throw error;
  }
}
